import { useDropzone } from 'react-dropzone';
import type { DropzoneOptions } from 'react-dropzone';
import type { FC } from 'react';
import { Divider, Flex, Icon, Text, Tooltip } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonIcon, ButtonText, useToast } from '@morressier/alembic';
import { faArrowUpFromBracket } from '@fortawesome/pro-solid-svg-icons';
import { downloadFile } from 'src/utils/file';
import { isLocal } from 'src/utils/env';

export type FileUploadProps = {
  uploadButtonText?: string;
  isInvalid?: boolean;
  isReadOnly?: boolean;
  file?: any;
} & DropzoneOptions;

export const SingleFileUpload: FC<FileUploadProps> = props => {
  const {
    multiple = true,
    noClick = true,
    isInvalid = false,
    isReadOnly = false,
    uploadButtonText = multiple ? 'Choose files' : 'Choose file',
    ...rest
  } = props;

  const toast = useToast();

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    multiple,
    noClick,
    disabled: isReadOnly,
    ...rest
  });

  const middleTruncate = (str: string, maxLength = 25) => {
    if (!str) return '';
    if (str.length <= maxLength) return str;

    const ellipsis = '...';
    const charsToShow = maxLength - ellipsis.length;
    const front = Math.ceil(charsToShow / 2);
    const back = Math.floor(charsToShow / 2);

    return `${str.slice(0, front)}${ellipsis}${str.slice(str.length - back)}`;
  };

  return (
    <Flex
      {...getRootProps()}
      border="1px solid"
      borderRadius={'small'}
      borderColor={isInvalid ? 'errorPrimaryBorder' : 'inputBorder'}
      background={isReadOnly ? 'inputFillReadOnly' : 'none'}
      flexDirection={'row'}
      justifyContent={props.file ? 'flex-start' : 'center'}
      width={'220px'}
    >
      <input {...getInputProps()} aria-label='File Upload'/>
      {isDragActive ? (
        <Text as="span" textStyle={'textBody'}>
          {multiple ? 'Drop file(s) here' : 'Drop file here'}
        </Text>
      ) : (
        <>
          {props?.file ? (
            <Flex align="center" justify="space-between" width="100%">
              <Flex flexGrow={1}>
                <Tooltip
                  hasArrow
                  placement="top"
                  label={`Download file`}
                  borderRadius={4}
                  shouldWrapChildren
                >
                  <ButtonText
                    padding={2}
                    size="small"
                    textStyle="headingItem"
                    color="actionPrimaryFill"
                    onClick={() => {
                      downloadFile(props.file.url).catch(_e => {
                        toast({
                          variant: 'error',
                          title: 'Error',
                          description: `Failed to obtain access to the file. ${
                            isLocal
                              ? 'Are you port-forwarding to asset-api?'
                              : ''
                          }`,
                          isClosable: true
                        });
                      });
                    }}
                    maxW="200px"
                  >
                    {middleTruncate(props?.file?.name, 18)}
                  </ButtonText>
                </Tooltip>
              </Flex>
              <Divider orientation="vertical" borderColor="gray.600" />
              <Flex align="center">
                <Tooltip
                  hasArrow
                  placement="top"
                  label={`Upload new file`}
                  borderRadius={4}
                  shouldWrapChildren
                >
                  <ButtonIcon
                    size="regular"
                    ariaLabel="downloader"
                    onClick={isReadOnly ? () => {} : open}
                    borderRadius="4px"
                    disabled={isReadOnly}
                    _disabled={{ color: 'actionPrimaryContentReadOnly' }}
                    icon={<FontAwesomeIcon icon={faArrowUpFromBracket} />}
                  />
                </Tooltip>
              </Flex>
            </Flex>
          ) : (
            <ButtonText
              onClick={isReadOnly ? () => {} : open}
              borderRadius={'4px'}
              leftIcon={
                <Icon as={FontAwesomeIcon} icon={faArrowUpFromBracket} />
              }
              disabled={isReadOnly}
              _disabled={{
                color: 'actionPrimaryContentReadOnly'
              }}
              size="small"
              width={'100%'}
              aria-label='This is button'
            >
              {uploadButtonText}
            </ButtonText>
          )}
        </>
      )}
    </Flex>
  );
};
